import IconPencil from "assets/img/icon-pencil.svg?react"

import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import Modal from "components/Modal/Modal"
import ArticleDetails from "components/ArticleDetails/ArticleDetails"
import Button from "components/Button/Button"
import Routes from "@/routes"
import useArticle from "hooks/useArticle"
import { Guid } from "@/types"
import confirmModals from "stores/confirmModals"
import ArticleInspectionSettings from "components/ArticleInspectionSettings/ArticleInspectionSettings"

export interface IArticleSidebarProps {
    articleId?: Guid
    getArticleId?(): Promise<Guid | null>
    onClose?(): void
}

const ArticleSidebar: FC<React.PropsWithChildren<IArticleSidebarProps>> = ({
    articleId: _articleId,
    getArticleId,
    onClose,
}) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [articleId, setArticleId] = useState<Guid | null>(null)
    const { article, refetchArticle } = useArticle(articleId, onClose)
    const editPath = article?.id ? Routes.articleEdit.getPath({ articleId: article.id }) : ""
    const [showInspectionSettingsModal, setShowInspectionSettingModal] = useState(false)


    useEffect(() => {
        if (_articleId) {
            setArticleId(_articleId)
            return
        }
        if (getArticleId) {
            (async () => {
                const foundArticleId = await getArticleId()
                if (foundArticleId) {
                    setArticleId(foundArticleId)
                } else if (typeof onClose === "function") {
                    onClose()
                }
            })()
        }
    }, [_articleId, getArticleId, onClose])

    function handleEditClick(event: MouseEvent) {
        event.preventDefault()
        if (!article || !article.id) return
        confirmModals.show({
            title: t("common.warning"),
            children: t("article.editConfirm"),
            confirmButtonLabel: t("common.confirm"),
            theme: "danger",
            maxWidth: "586px",
            onConfirm: close => {
                close?.()
                if (event.ctrlKey || event.metaKey) {
                    Object.assign(document.createElement("a"), {
                        target: "_blank",
                        rel: "noopener noreferrer",
                        href: editPath,
                    }).click()
                } else {
                    navigate(editPath)
                }
            }
        })
    }

    return (
        <Modal type="sidebar"
            title={t('article.articleDetails')}
            isBusy={!article}
            onClose={onClose}
            closeButton={!article?.canUpdate}
            toolbar={
                article?.canUpdate ?
                    <Button
                        className="ml-2"
                        round
                        theme="grey"
                        type="link"
                        url={editPath}
                        ariaLabel={t('common.edit')}
                        icon={<IconPencil />}
                        onClick={handleEditClick}
                    />
                    : undefined
            }>
            {!!article &&
                <ArticleDetails
                    header
                    article={article}
                    descriptionClamp={3}
                    adjustableDescription
                    onEditInspectionSetting={article.linkUpdateInspectionSettings ? () => setShowInspectionSettingModal(true) : undefined}
                />
            }

            {showInspectionSettingsModal && article &&
                <ArticleInspectionSettings
                    onClose={() => setShowInspectionSettingModal(false)}
                    article={article}
                    onUpdated={refetchArticle}
                />}
        </Modal>
    )
}

export default ArticleSidebar
